import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useState, type FormEvent } from 'react';
import { BUSINESS_TYPES, businessType, type Terminology, type WorkspaceDTO } from '@regent/shared';
import { Button, Field, Input } from '@/components/ui';
import { AuthLayout } from '@/features/auth/auth-layout';
import { BusinessTypePicker, TerminologyFields } from '@/features/settings/terminology-fields';
import { api, errorMessage } from '@/lib/api';
import { authClient } from '@/lib/auth';
import { keys, meQuery, useMe } from '@/lib/queries';

export const Route = createFileRoute('/onboarding')({
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(meQuery);
    if (!me) throw redirect({ to: '/login' });
  },
  component: OnboardingPage,
});

const DEFAULT_TYPE = BUSINESS_TYPES.find((b) => b.id === 'other')!;

function OnboardingPage() {
  const me = useMe();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [typeId, setTypeId] = useState<string | null>(null);
  const [terms, setTerms] = useState<Terminology>(DEFAULT_TYPE.terms);
  const [customized, setCustomized] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const first = !me?.workspaces.length;

  const pickType = (id: string) => {
    setTypeId(id);
    const preset = businessType(id);
    if (preset && !customized) setTerms(preset.terms);
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setPending(true);
    try {
      const { workspace } = await api.post<{ workspace: WorkspaceDTO }>('/workspaces', {
        name,
        ...(typeId ? { businessType: typeId } : {}),
        terminology: terms,
      });
      await qc.invalidateQueries({ queryKey: keys.me });
      await navigate({ to: '/w/$workspaceId/home', params: { workspaceId: workspace.id } });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setPending(false);
    }
  };

  const termsValid = (['space', 'folder', 'list'] as const).every(
    (k) => terms[k].singular.trim() && terms[k].plural.trim(),
  );

  return (
    <AuthLayout
      width="md"
      title={first ? 'Create your workspace' : 'Create a workspace'}
      subtitle="Tell us a little about the business and we will set up names that fit."
      footer={
        <button
          className="text-fg-muted hover:text-fg hover:underline"
          onClick={async () => {
            await authClient.signOut();
            await qc.resetQueries({ queryKey: keys.me });
            await navigate({ to: '/login' });
          }}
        >
          Signed in as {me?.user.email}. Sign out
        </button>
      }
    >
      <form onSubmit={submit} className="space-y-5" noValidate>
        <Field
          label="Workspace name"
          htmlFor="workspace-name"
          hint="Usually your company or team name"
          error={error}
        >
          <Input
            id="workspace-name"
            autoFocus
            required
            placeholder="Digital King"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="h-9"
          />
        </Field>

        <Field label="Type of business" htmlFor="business-type" hint="Optional. It only suggests the names below.">
          <BusinessTypePicker value={typeId} onChange={pickType} className="w-full" />
        </Field>

        <div>
          <span className="mb-1 block text-sm font-medium text-fg">What should we call things?</span>
          <p className="mb-2 text-xs text-fg-muted">
            Work is organized in three levels. Name them the way your team already talks. You can change
            this any time in Settings.
          </p>
          <TerminologyFields
            value={terms}
            onChange={(next) => {
              setCustomized(true);
              setTerms(next);
            }}
          />
        </div>

        <Button
          type="submit"
          variant="primary"
          size="lg"
          className="w-full"
          loading={pending}
          disabled={!name.trim() || !termsValid}
        >
          Create workspace
        </Button>
        {!first ? (
          <Button variant="ghost" className="w-full" onClick={() => void navigate({ to: '/' })}>
            Cancel
          </Button>
        ) : null}
      </form>
    </AuthLayout>
  );
}
